<script setup lang="ts">
import {
  PhCheckCircle as CheckCircleIcon,
  PhLockKey as LockKeyIcon,
  PhPhone as PhoneIcon,
  PhShieldCheck as ShieldCheckIcon,
} from '@phosphor-icons/vue'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiError, api } from '../services/api'
import { useSessionStore } from '../stores/session'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const phone = ref('')
const code = ref('')
const codeSent = ref(false)
const sending = ref(false)
const verifying = ref(false)
const countdown = ref(0)
const message = ref('')
const error = ref('')
const isDev = import.meta.env.DEV
let timer = 0

const phoneValid = computed(() => /^1\d{10}$/.test(phone.value))
const canSend = computed(() => phoneValid.value && countdown.value === 0 && !sending.value)
const canVerify = computed(() => phoneValid.value && /^\d{6}$/.test(code.value) && !verifying.value)
const loginTitle = computed(() => {
  const intent = queryText(route.query.intent).toLowerCase()
  const redirect = queryText(route.query.redirect)
  const inquiryIntent = ['inquiry', 'quote', 'quotation', 'customization', '询价', '定制']
    .some((keyword) => intent.includes(keyword))
  const inquiryRedirect = /^\/products\/[^/?#]+(?:[/?#]|$)/i.test(redirect)
    || /(?:^|[?&])(?:intent|action)=(?:inquiry|quote|quotation)(?:&|$)/i.test(redirect)
  return inquiryIntent || inquiryRedirect
    ? (intent.includes('customization') || intent.includes('定制')
        ? '登录后提交您的定制方案'
        : '登录后提交您的询价')
    : '登录后查看订单进度'
})

function queryText(value: string | (string | null)[] | null | undefined) {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string').join(' ')
  return typeof value === 'string' ? value : ''
}

function startCountdown(seconds: number) {
  countdown.value = seconds
  window.clearInterval(timer)
  timer = window.setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) window.clearInterval(timer)
  }, 1000)
}

async function sendCode() {
  if (!canSend.value) return
  sending.value = true
  error.value = ''
  message.value = ''
  try {
    const result = await api.sendSms(phone.value)
    codeSent.value = true
    startCountdown(result.retryAfterSeconds)
    message.value = result.demoCode
      ? `演示验证码为 ${result.demoCode}`
      : '验证码已发送，请查看手机短信。'
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '验证码发送失败'
  } finally {
    sending.value = false
  }
}

async function verify() {
  if (!canVerify.value) return
  verifying.value = true
  error.value = ''
  try {
    const result = await api.verifySms(phone.value, code.value)
    session.signIn(result.customerId, result.phone, result.accessToken)
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/orders'
    await router.replace(redirect)
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : '登录失败，请重试。'
  } finally {
    verifying.value = false
  }
}

onBeforeUnmount(() => window.clearInterval(timer))
</script>

<template>
  <section class="auth-page">
    <div class="container auth-layout">
      <div class="auth-intro">
        <ShieldCheckIcon :size="46" weight="duotone" aria-hidden="true" />
        <h1>{{ loginTitle }}</h1>
        <p>手机号用于查看您的询价进度、量房安排和付款状态。</p>
        <ul>
          <li><CheckCircleIcon :size="19" weight="fill" />只能查看本人订单</li>
          <li><CheckCircleIcon :size="19" weight="fill" />关键确认需要重新验证</li>
          <li><CheckCircleIcon :size="19" weight="fill" />您的信息仅本人可见</li>
        </ul>
      </div>
      <form class="auth-card" @submit.prevent="verify">
        <div>
          <span class="eyebrow">手机号登录</span>
          <h2>输入手机号，一步登录</h2>
          <p v-if="isDev">开发环境连接不到短信服务时，会显示演示验证码。</p>
        </div>
        <label>
          <span>手机号</span>
          <div class="input-with-icon">
            <PhoneIcon :size="20" aria-hidden="true" />
            <input
              v-model="phone"
              type="tel"
              inputmode="numeric"
              maxlength="11"
              autocomplete="tel"
              placeholder="请输入11位手机号"
            />
          </div>
          <small class="auth-card__hint">首次使用直接填写手机号即可，系统自动为您创建账号。</small>
          <small v-if="phone && !phoneValid">请输入有效的中国大陆手机号</small>
        </label>
        <label>
          <span>短信验证码</span>
          <div class="code-field">
            <div class="input-with-icon">
              <LockKeyIcon :size="20" aria-hidden="true" />
              <input
                v-model="code"
                type="text"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
                placeholder="6位验证码"
              />
            </div>
            <button class="button button--secondary" type="button" :disabled="!canSend" @click="sendCode">
              {{ sending ? '发送中' : countdown ? `${countdown} 秒` : codeSent ? '重新发送' : '获取验证码' }}
            </button>
          </div>
        </label>
        <p v-if="message" class="form-success" role="status">{{ message }}</p>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="button button--primary button--block" type="submit" :disabled="!canVerify">
          {{ verifying ? '正在验证' : '登录并继续' }}
        </button>
        <p class="auth-card__legal">登录表示你同意仅将手机号用于订单服务与必要通知。</p>
      </form>
    </div>
  </section>
</template>
